<template>
  <v-card outlined>
    <v-card-title class="py-2">
      <v-icon left small>mdi-cart</v-icon>{{ use_language.shopping_cart }}
      <v-chip class="ml-2" small>{{ items.length }}</v-chip>
      <v-spacer />
      <v-btn small text color="red" :disabled="!items.length" @click="$emit('clear')">
        <v-icon left small>mdi-delete-sweep</v-icon>{{ use_language.clear }}
      </v-btn>
    </v-card-title>
    <v-divider />

    <div v-if="!items.length" class="text-center grey--text py-8 caption">
      {{ use_language.no_products_yet }}<br />{{ use_language.scan_or_select_left }}
    </div>

    <div v-else ref="list" class="cart-list">
      <div
        v-for="(it, i) in items"
        :key="i"
        class="cart-row"
        :class="{ 'cart-row--flash': i === flashIndex }"
      >
        <div class="d-flex">
          <v-img
            v-if="it.photoLink"
            :src="it.photoLink"
            width="40"
            height="40"
            contain
            class="cart-img flex-grow-0 flex-shrink-0"
          />
          <div
            v-else
            class="cart-img d-flex align-center justify-center flex-grow-0 flex-shrink-0"
          >
            <v-icon x-small color="grey lighten-1">mdi-image-off</v-icon>
          </div>

          <div class="flex-grow-1 ml-2 min-w-0">
            <div class="d-flex align-start">
              <div class="cart-name flex-grow-1" :title="it.name">{{ it.name }}</div>
              <v-btn icon x-small class="ml-1" @click="$emit('remove', i)">
                <v-icon small color="red">mdi-close</v-icon>
              </v-btn>
            </div>
            <div class="cart-sku">{{ it.sku || '-' }} / {{ it.barcode || '-' }}</div>
          </div>
        </div>

        <div class="d-flex mt-2">
          <div class="qty-col mr-2">
            <div class="cart-label">{{ use_language.quantity }}</div>
            <div class="d-flex align-center">
              <v-btn icon x-small @click="dec(i)"><v-icon small>mdi-minus</v-icon></v-btn>
              <v-text-field
                :value="it.quantityInput"
                type="number"
                min="1"
                hide-details
                dense
                class="cart-qty mx-1 pt-0 mt-0"
                @input="setQty(i, $event)"
              />
              <v-btn icon x-small @click="inc(i)"><v-icon small>mdi-plus</v-icon></v-btn>
            </div>
          </div>
          <div class="flex-grow-1 mr-2">
            <div class="cart-label">{{ use_language.price }}</div>
            <v-text-field
              :value="it.salePrice"
              type="number"
              min="0"
              hide-details
              dense
              class="cart-price pt-0 mt-0"
              @input="setPrice(i, $event)"
            />
          </div>
          <div class="flex-grow-1">
            <div class="cart-label">{{ use_language.discount_per_piece }}</div>
            <v-text-field
              :value="it.discount"
              type="number"
              min="0"
              hide-details
              dense
              class="cart-price pt-0 mt-0"
              @input="setDiscount(i, $event)"
            />
          </div>
        </div>

        <div class="d-flex align-center justify-end mt-2">
          <span class="cart-label mr-2 mb-0">{{ use_language.sum_total }}</span>
          <span class="cart-total">{{ formatPrice(lineTotal(it)) }}</span>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import { get_languages } from '@/website/global_function'

export default {
  name: 'PosCart',
  props: {
    items: { type: Array, default: () => [] }
  },
  data: () => ({
    use_language: {},
    flashIndex: -1,
    flashTimer: null
  }),
  beforeDestroy () {
    if (this.flashTimer) clearTimeout(this.flashTimer)
  },
  async created () {
    this.use_language = await this.get_languages(localStorage.getItem('set_language'))
  },
  methods: {
    get_languages,
    scrollToItem (index) {
      this.flashIndex = index
      if (this.flashTimer) clearTimeout(this.flashTimer)
      this.flashTimer = setTimeout(() => { this.flashIndex = -1 }, 1200)

      this.$nextTick(() => {
        const list = this.$refs.list
        if (!list) return
        const row = list.children[index]
        if (!row) return
        row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
    },
    inc (i) {
      const it = this.items[i]
      this.update(i, { quantityInput: Number(it.quantityInput || 0) + 1 })
    },
    dec (i) {
      const it = this.items[i]
      const next = Math.max(1, Number(it.quantityInput || 1) - 1)
      this.update(i, { quantityInput: next })
    },
    setQty (i, v) {
      const next = Math.max(1, Number(v) || 1)
      this.update(i, { quantityInput: next })
    },
    setPrice (i, v) {
      this.update(i, { salePrice: Number(v) || 0 })
    },
    setDiscount (i, v) {
      this.update(i, { discount: Number(v) || 0 })
    },
    update (i, patch) {
      this.$emit('update-item', { index: i, patch })
    },
    lineTotal (it) {
      const gross = Number(it.salePrice || 0) * Number(it.quantityInput || 0)
      const disc = Number(it.discount || 0)
      return Math.max(0, gross - disc)
    },
    formatPrice (v) {
      return Number(v || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }
}
</script>

<style scoped>
.cart-list {
  max-height: 42vh;
  overflow-y: auto;
}
.cart-row {
  padding: 8px 10px;
  border-bottom: 1px solid #cfd8dc;
}
.cart-row:last-child {
  border-bottom: none;
}
/* ไฮไลต์สั้น ๆ ให้รู้ว่าเพิ่งเพิ่ม/เพิ่มจำนวนชิ้นไหน */
.cart-row--flash {
  animation: cart-flash 1.2s ease-out;
}
@keyframes cart-flash {
  0% { background-color: #fff8e1; }
  100% { background-color: transparent; }
}
.min-w-0 {
  min-width: 0;
}
.cart-name {
  font-size: 12px;
  line-height: 1.25;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.cart-sku {
  font-size: 10px;
  color: #90a4ae;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cart-total {
  font-size: 13px;
  font-weight: 600;
}
.cart-label {
  font-size: 10px;
  color: #90a4ae;
  line-height: 1;
  margin-bottom: 2px;
}
.qty-col {
  width: 108px;
  flex: 0 0 108px;
}
.cart-qty >>> input {
  text-align: center;
  padding: 0;
}
.cart-price >>> input { text-align: right; }
.cart-img {
  width: 40px;
  height: 40px;
  border: 1px solid #eceff1;
  border-radius: 4px;
  background: #fafafa;
}
</style>
